import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '@/components/Logo';
import ThemeToggle from '@/components/ThemeToggle';
import SEO from '@/components/SEO';

const PrivacyPage = () => (
  <div className="min-h-screen bg-background">
    <SEO title="Privacy Policy" description="How Poettik collects, uses and protects your personal data — GDPR-compliant for UK and EU users." />
    <nav className="border-b border-border bg-card">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/"><Logo /></Link>
        <ThemeToggle />
      </div>
    </nav>
    <main className="max-w-3xl mx-auto px-4 py-12 prose prose-sm dark:prose-invert">
      <h1>Privacy Policy</h1>
      <p className="text-muted-foreground">Last updated: June 2026</p>

      <h2>1. Who We Are</h2>
      <p>Poettik ("we", "us", "our") operates the website poettik.com. Poettik is a trading name of VZV Ltd, a company registered in England and Wales (Company No. 10689932). VZV Ltd is the data controller responsible for your personal data, and we are committed to protecting your data and your privacy.</p>

      <h2>2. Information We Collect</h2>
      <p>We collect the following information when you use our service:</p>
      <ul>
        <li><strong>Account information:</strong> Name, email address, and encrypted password when you register.</li>
        <li><strong>Payment information:</strong> Processed securely by Stripe. We do not store your card details.</li>
        <li><strong>Usage data:</strong> Products you generate content for, generation counts, and feature usage to improve our service.</li>
        <li><strong>Technical data:</strong> IP address, browser type, and device information collected via cookies.</li>
        <li><strong>Free tool data:</strong> When you use our free viral video transcriber (no account needed), we store the email address you optionally provide, along with a randomly generated device identifier and your IP address. These are used to apply fair-use limits and, where you provide an email, to contact you about Poettik.</li>
      </ul>

      <h2>3. How We Use Your Information</h2>
      <ul>
        <li>To provide and maintain our content generation service.</li>
        <li>To process your subscription payments.</li>
        <li>To send you verification emails and service notifications.</li>
        <li>To improve our AI-generated content quality.</li>
        <li>To prevent fraud and abuse of free trials and free tools.</li>
        <li>To send you occasional marketing about Poettik if you provided your email to our free transcriber tool (you can opt out at any time by contacting us).</li>
      </ul>

      <h2>4. Data Sharing</h2>
      <p>We do not sell your personal data. We share data only with:</p>
      <ul>
        <li><strong>Stripe:</strong> For payment processing.</li>
        <li><strong>AI Providers:</strong> The product details and text you submit are sent to AI models to generate content and assessments. Please avoid entering sensitive personal information in free-text fields, as whatever you submit is processed by these providers to produce your results.</li>
        <li><strong>Transcription &amp; data providers:</strong> When you use features such as the video transcriber, the Creator Dashboard or trending products, we send the video links, public account handles or product details you request to specialist third-party providers that return transcripts and public performance data. We only send what is needed to deliver the feature.</li>
        <li><strong>Email Service:</strong> Your email address is used to send verification codes and service notifications.</li>
        <li><strong>Meta (Facebook) — advertising, with consent only:</strong> If you accept marketing cookies, we use the Meta Pixel to measure our advertising. It may share limited event data (such as page views or completed subscriptions) with Meta Platforms, Inc. It is never loaded if you decline.</li>
      </ul>
      <p><strong>International transfers:</strong> Some of these providers (for example certain AI and transcription services) may process your data outside the UK and European Economic Area. Where that happens, we rely on appropriate safeguards such as the UK International Data Transfer Agreement or the EU Standard Contractual Clauses to protect your data.</p>

      <h2>5. Data Retention</h2>
      <p>We retain your account data for as long as your account is active. You may request deletion of your account and associated data by contacting us at hey@poettik.com.</p>

      <h2>6. Your Rights (UK / EU GDPR)</h2>
      <p>Under UK and EU data protection law, you have the right to:</p>
      <ul>
        <li>Access your personal data.</li>
        <li>Rectify inaccurate data.</li>
        <li>Request erasure of your data.</li>
        <li>Object to processing of your data.</li>
        <li>Data portability.</li>
      </ul>
      <p>To exercise any of these rights, contact us at hey@poettik.com.</p>

      <h2>7. Your Rights (California Residents — CCPA / CPRA)</h2>
      <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA):</p>
      <ul>
        <li><strong>Right to know:</strong> request what categories of personal information we have collected about you.</li>
        <li><strong>Right to delete:</strong> request that we delete your personal information.</li>
        <li><strong>Right to correct:</strong> request that we correct inaccurate personal information.</li>
        <li><strong>Right to opt-out:</strong> we <strong>do not sell or share</strong> your personal information with third parties for advertising purposes, so no opt-out is necessary.</li>
        <li><strong>Right to non-discrimination:</strong> we will not deny services or charge different prices for exercising any of these rights.</li>
      </ul>
      <p>To submit a verifiable consumer request, email hey@poettik.com with the subject line &quot;CCPA Request&quot;.</p>

      <h2>8. Security</h2>
      <p>We use industry-standard security measures including encrypted passwords (bcrypt), HTTPS encryption, and secure payment processing via Stripe.</p>

      <h2>9. Children</h2>
      <p>Our service is not intended for children under 16. We do not knowingly collect data from children.</p>

      <h2>10. Changes to This Policy</h2>
      <p>We may update this policy from time to time. We will notify you of any significant changes via email or a notice on our website.</p>

      <h2>11. Contact Us</h2>
      <p>If you have any questions about this privacy policy, please contact us at <a href="mailto:hey@poettik.com" className="text-primary">hey@poettik.com</a>.</p>
    </main>
  </div>
);

export default PrivacyPage;
